import { useEffect, useMemo, useState, memo, useCallback } from 'react';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import { cn } from '@/utils/cn';
import { useForm } from 'react-hook-form';
import {
  assignLabelCustomerValidationSchema,
  TAssignLabelCustomerValidation,
  useAssignLabelCustomerMutation,
} from '../../../api/assign-label-customer.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleDomBubbleEvent } from '@/utils/dom';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { SearchIcon } from 'lucide-react';
import { useGetListLabelCustomerQuery } from '../../../api/get-list-label-customer.api';
import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import { TLabelCustomer } from '../../../types/label-customer.type';
import { createAssignLabelCustomerOptimisticUpdate } from './assign-label-customer-optimistic-update';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { Input } from '@/components/_ui/input';

// Type definition for customer label structure
type CustomerLabel = {
  labelCustomerId: string;
  name: string;
  color: string;
};

export type TAssignLabelCustomerModalType = 'AssignLabelCustomerModal';
export interface IAssignLabelCustomerModalProps {
  customerId: string;
  caseId?: string;
  labelCustomerIds?: TLabelCustomer['labelCustomerId'][];
}

/**
 * AssignLabelCustomerModal Component
 *
 * This modal allows users to assign or remove customer labels from a case.
 * Features:
 * - Search through available customer labels
 * - Select/deselect multiple labels with checkboxes
 * - Visual color indicators for each label
 * - Pre-populated with existing labels when editing
 */
const AssignLabelCustomerModal = memo((props: IAssignLabelCustomerModalProps) => {
  const showToast = useCustomToast();
  const customerModuleModalRootActions = useCustomerModuleModalRootActionsStore();

  const form = useForm<TAssignLabelCustomerValidation>({
    mode: 'onChange',
    resolver: zodResolver(assignLabelCustomerValidationSchema),
  });

  // Check if modal is open
  const isOpenAssignLabelCustomerModal = useMemo(() => {
    return customerModuleModalRootActions.checkIsOpenModal('AssignLabelCustomerModal');
  }, [customerModuleModalRootActions]);

  // Local state for search functionality and selected labels
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedLabelCustomers, setSelectedLabelCustomers] = useState<TLabelCustomer['labelCustomerId'][]>([]);

  // Initialize selected labels when modal opens with default values
  useEffect(() => {
    if (props?.labelCustomerIds || props?.caseId || props?.customerId) {
      // Map default values to our local state structure

      form.reset({
        customerId: props.customerId,
        labelIds: props.labelCustomerIds || [],
      });
      setSelectedLabelCustomers(props.labelCustomerIds || []);
    }
  }, [props, form]);

  // Fetch all available customer labels from API
  const { data: labelCustomerData, isLoading: _isLoadingLabelCustomer } = useGetListLabelCustomerQuery({
    params: {
      page: 1,
      limit: 500, // Fetch a large number to show all available labels
      search: '',
      sortBy: 'createdAt',
      sortDir: 'DESC',
    },
    queryConfig: {
      enabled: isOpenAssignLabelCustomerModal,
    },
  });

  // Extract label options from API response
  const availableLabelOptions = useMemo(() => {
    return (
      labelCustomerData?.payload?.rows?.filter((item: TLabelCustomer) =>
        item?.name?.toLowerCase().includes(searchQuery?.toLowerCase())
      ) || []
    );
  }, [labelCustomerData, searchQuery]);

  /**
   * Handle selection/deselection of a customer label
   * @param labelToToggle - The customer label to toggle selection for
   */
  const handleLabelSelectionToggle = useCallback(
    (labelToToggle: CustomerLabel) => {
      const isLabelCurrentlySelected = selectedLabelCustomers.some(
        (selectedLabelCustomerId) => selectedLabelCustomerId === labelToToggle.labelCustomerId
      );

      if (isLabelCurrentlySelected) {
        // Remove label from selection if already selected
        const updatedSelection = selectedLabelCustomers.filter(
          (selectedLabelCustomerId) => selectedLabelCustomerId !== labelToToggle.labelCustomerId
        );
        form.setValue('labelIds', updatedSelection);
        setSelectedLabelCustomers(updatedSelection);
      } else {
        // Add label to selection if not currently selected
        const updatedSelection = [...selectedLabelCustomers, labelToToggle.labelCustomerId];
        form.setValue('labelIds', updatedSelection);
        setSelectedLabelCustomers(updatedSelection);
      }
    },
    [selectedLabelCustomers, form]
  );

  /**
   * Check if a specific label is currently selected
   * @param labelToCheck - The customer label to check selection status for
   * @returns boolean indicating if the label is selected
   */
  const isLabelSelected = useCallback(
    (labelToCheck: CustomerLabel): boolean => {
      return selectedLabelCustomers.some((selectedLabelId) => selectedLabelId === labelToCheck.labelCustomerId);
    },
    [selectedLabelCustomers]
  );

  /**
   * Check if all available labels are currently selected
   * @returns boolean indicating if all labels are selected
   */
  const isAllSelected = useCallback((): boolean => {
    return availableLabelOptions.length > 0 && selectedLabelCustomers.length === availableLabelOptions.length;
  }, [availableLabelOptions, selectedLabelCustomers]);

  /**
   * Handle selection/deselection of all labels
   */
  const handleLabelSelectionAllToggle = useCallback(() => {
    if (isAllSelected()) {
      // If all are selected, deselect all
      form.setValue('labelIds', []);
      setSelectedLabelCustomers([]);
    } else {
      // If not all are selected, select all
      const allLabelIds = availableLabelOptions.map((label: TLabelCustomer) => label.labelCustomerId);
      form.setValue('labelIds', allLabelIds);
      setSelectedLabelCustomers(allLabelIds);
    }
  }, [isAllSelected, availableLabelOptions, form]);

  // Get all available labels (not filtered) for mapping labelIds to full objects
  const allAvailableLabels = useMemo(() => {
    return labelCustomerData?.payload?.rows || [];
  }, [labelCustomerData]);

  // Create optimistic update handlers
  const optimisticUpdateHandlers = useMemo(
    () =>
      createAssignLabelCustomerOptimisticUpdate({
        caseId: props?.caseId,
        customerId: props?.customerId,
        allAvailableLabels,
      }),
    [props?.caseId, props?.customerId, allAvailableLabels]
  );

  const mutationAssignLabelCustomer = useAssignLabelCustomerMutation({
    mutationConfigOptions: {
      onMutate: optimisticUpdateHandlers.onMutate,
      onError: (error, variables, context) => {
        const { errorMessage } = optimisticUpdateHandlers.onError(error, variables, context);
        showToast({
          type: 'error',
          message: errorMessage,
        });
      },
      onSuccess: () => {
        optimisticUpdateHandlers.onSuccess();
        form.reset();
        setSelectedLabelCustomers([]);
        customerModuleModalRootActions.closeModal('AssignLabelCustomerModal');
        showToast({
          type: 'success',
          message: 'Customer labels assigned successfully',
        });
      },
    },
  });

  const handleSubmitAssignLabelCustomer = useCallback(
    (data: TAssignLabelCustomerValidation) => {
      mutationAssignLabelCustomer.mutate({
        customerId: data.customerId,
        labelIds: data.labelIds,
      });
    },
    [mutationAssignLabelCustomer]
  );

  const handleClose = useCallback(() => {
    customerModuleModalRootActions.closeModal('AssignLabelCustomerModal');
    form.reset();
    setSelectedLabelCustomers([]);
  }, [customerModuleModalRootActions, form]);

  return (
    <Modal
      isOpen={isOpenAssignLabelCustomerModal}
      lockBodyScroll
      onClose={handleClose}
      isCloseOutside={false}
      className="w-[400px]"
    >
      <ModalHeader>
        <ModalTitle>Select Label Customer</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="flex flex-col gap-[12px]">
          {/* Search input for filtering labels */}
          <div>
            <Input
              startIcon={<SearchIcon className="w-[16px] h-[16px]" />}
              placeholder="Search label customer..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>

          {/* Scrollable container for label options */}
          <div className="flex flex-col border border-[#D9D9D9] rounded-[8px] overflow-hidden">
            {/* Sticky "Select All" header */}
            <div className="flex items-center justify-between gap-[20px] bg-[#0C8DBF] px-[16px] py-[12px] sticky top-0 z-10">
              <div className="flex items-center gap-[16px]">
                <Checkbox
                  className="border-white bg-white outline-hidden  data-[state=checked]:text-[#0C8DBF] data-[state=checked]:bg-white data-[state=checked]:border-[#0C8DBF] data-[state=unchecked]:text-white data-[state=unchecked]:bg-white data-[state=unchecked]:border-[#D9D9D9]"
                  checked={isAllSelected()}
                  onCheckedChange={handleLabelSelectionAllToggle}
                />
                <span className="text-[12px] font-normal text-white">Select All</span>
              </div>
              <div className="flex items-center gap-[12px]">
                <span className="text-[12px] font-normal text-white">
                  {selectedLabelCustomers.length} / {availableLabelOptions.length}
                </span>
              </div>
            </div>

            {/* Scrollable content area */}
            <div className="max-h-[200px] overflow-y-auto">
              {availableLabelOptions.map((labelOption: TLabelCustomer) => {
                const isCurrentlySelected = isLabelSelected(labelOption);

                return (
                  <div
                    key={labelOption.labelCustomerId}
                    className="flex items-center gap-[20px] cursor-pointer hover:bg-gray-50   transition-colors px-[16px] py-[12px] border-b border-[#D9D9D9]"
                    onClick={() => handleLabelSelectionToggle(labelOption)}
                  >
                    {/* Checkbox to show selection state */}
                    <Checkbox
                      checked={isCurrentlySelected}
                      // Note: Selection is handled via parent div onClick, not checkbox onChange
                    />

                    {/* Color indicator for the label */}
                    <div
                      className={cn(`w-[12px] h-[12px] rounded-full border border-[#${labelOption.color}]`)}
                      style={{ backgroundColor: labelOption.color }}
                    />

                    {/* Label name */}
                    <span className="text-[12px] font-normal flex-1">{labelOption.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <form
            id="assign-label-customer-form"
            onSubmit={(e) => {
              handleDomBubbleEvent(e);
              form.handleSubmit(handleSubmitAssignLabelCustomer)(e);
            }}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="assign-label-customer-form"
          type="submit"
          disabled={mutationAssignLabelCustomer.isPending}
        >
          {mutationAssignLabelCustomer.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Assign
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

AssignLabelCustomerModal.displayName = 'AssignLabelCustomerModal';
export default AssignLabelCustomerModal;
